import { Package, Camera } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CONDITION_LABELS = { neuf:"Comme neuf", bon:"Bon etat", moyen:"Etat moyen", pieces:"Pour pieces" };
const SERVICE_LABELS = { sav:"Service apres-vente", livraison:"Livraison & Installation", montage:"Montage & Formation" };
const WILAYAS = ["01 - Adrar","02 - Chlef","03 - Laghouat","04 - Oum El Bouaghi","05 - Batna","06 - Béjaïa","07 - Biskra","08 - Béchar","09 - Blida","10 - Bouira","11 - Tamanrasset","12 - Tébessa","13 - Tlemcen","14 - Tiaret","15 - Tizi Ouzou","16 - Alger","17 - Djelfa","18 - Jijel","19 - Sétif","20 - Saïda","21 - Skikda","22 - Sidi Bel Abbès","23 - Annaba","24 - Guelma","25 - Constantine","26 - Médéa","27 - Mostaganem","28 - M'Sila","29 - Mascara","30 - Ouargla","31 - Oran","32 - El Bayadh","33 - Illizi","34 - Bordj Bou Arréridj","35 - Boumerdès","36 - El Tarf","37 - Tindouf","38 - Tissemsilt","39 - El Oued","40 - Khenchela","41 - Souk Ahras","42 - Tipaza","43 - Mila","44 - Aïn Defla","45 - Naâma","46 - Aïn Témouchent","47 - Ghardaïa","48 - Relizane","49 - Timimoun","50 - Bordj Badji Mokhtar","51 - Ouled Djellal","52 - Béni Abbès","53 - In Salah","54 - In Guezzam","55 - Touggourt","56 - Djanet","57 - El M'Ghair","58 - El Meniaa","59 - Aflou","60 - Barika","61 - Ksar Chellala","62 - Messaâd","63 - Aïn Oussera","64 - Bou Saâda","65 - El Abiodh Sidi Cheikh","66 - El Kantara","67 - Bir El Ater","68 - Ksar El Boukhari","69 - El Aricha"];

const img = (u) => u && u.startsWith('/api') ? API + u : u;

const ListingCard = ({ listing, onClick }) => (
  <div onClick={() => onClick(listing)} className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden cursor-pointer hover:border-[#1F4D3F] hover:shadow-md transition-all">
    <div className="h-40 bg-[#F5F1EA] flex items-center justify-center overflow-hidden">
      {listing.images && listing.images.length > 0
        ? <img src={img(listing.images[0])} alt={listing.title} className="w-full h-full object-cover" />
        : <Package className="w-10 h-10 text-[#C9BFAE]" />}
    </div>
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {listing.condition && <span className="text-xs text-[#7A6F5C] bg-[#F5F1EA] px-2 py-1 rounded-full">{CONDITION_LABELS[listing.condition]}</span>}
        {(listing.services || []).length > 0 && <span className="text-xs text-[#1F4D3F] bg-[#E4EDE9] px-2 py-1 rounded-full">+{listing.services.length} service(s)</span>}
      </div>
      <h3 className="font-semibold text-[#2D2A26] mb-1 line-clamp-2">{listing.title}</h3>
      {listing.brand && <div className="text-xs text-[#C75B27] mb-1">{listing.brand}</div>}
      <div className="text-xs text-[#9A8F82] mb-3">📍 {listing.wilaya || "Algerie"}</div>
      <div className="flex items-center justify-between">
        {listing.price ? <div className="font-bold text-[#1F4D3F]">{Math.round(listing.price).toLocaleString('fr')} DA{listing.price_negotiable ? " (neg.)" : ""}</div> : <div className="text-sm text-[#9A8F82]">Prix sur demande</div>}
        <div className="text-xs text-[#9A8F82]">👁 {listing.views || 0}</div>
      </div>
    </div>
  </div>
);

const ChatBox = ({ listing, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [msgImg, setMsgImg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages/" + listing.id, { headers: { "Authorization": "Bearer " + token } });
      if (r.ok) setMessages(await r.json());
    } catch {}
  };
  useEffect(() => { if (user) { fetchMessages(); const i = setInterval(fetchMessages, 4000); return () => clearInterval(i); } }, [listing, user]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem("dz_token");
      const data = new FormData(); data.append("file", file);
      const r = await fetch(API + "/api/upload", { method: "POST", headers: { "Authorization": "Bearer " + token }, body: data });
      if (r.ok) { const d = await r.json(); setMsgImg(d.url); }
    } catch {}
    setUploading(false);
  };

  const send = async () => {
    if ((!text.trim() && !msgImg) || sending) return;
    if (!user) { navigate("/connexion"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ listing_id: listing.id, text: text.trim(), image: msgImg }),
      });
      if (r.ok) { setText(""); setMsgImg(""); fetchMessages(); }
    } catch {}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col" style={{maxHeight:"85vh"}}>
        <div className="bg-[#1F4D3F] px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <div className="text-white font-semibold text-sm">Contacter le vendeur</div>
            <div className="text-white/70 text-xs truncate">{listing.title}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFAF8]" style={{minHeight:"300px"}}>
          {messages.length === 0 && <div className="text-center text-sm text-[#9A8F82] py-8">Envoyez un message au vendeur</div>}
          {messages.map(m => (
            <div key={m.id} className={"flex " + (m.from_id === user?.user_id ? "justify-end" : "justify-start")}>
              <div className={"max-w-[80%] px-3 py-2 rounded-xl text-sm " + (m.from_id === user?.user_id ? "bg-[#1F4D3F] text-white" : "bg-white border border-[#E8E0D0] text-[#2D2A26]")}>
                {m.from_id !== user?.user_id && <div className="text-xs font-medium text-[#C75B27] mb-1">{m.from_name}</div>}
                {m.image && <img src={img(m.image)} alt="" className="rounded-lg mb-1 max-w-full" style={{maxHeight:"150px"}} />}
                {m.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        {msgImg && (
          <div className="px-3 py-2 bg-[#F5F1EA] flex items-center gap-2">
            <img src={img(msgImg)} alt="" className="w-12 h-12 rounded object-cover" />
            <span className="text-xs text-[#7A6F5C] flex-1">Photo prête</span>
            <button onClick={() => setMsgImg("")} className="text-red-500 text-sm">Retirer</button>
          </div>
        )}
        <div className="p-3 border-t border-[#E8E0D0] flex gap-2 bg-white rounded-b-2xl">
          {user && <label className="cursor-pointer flex items-center px-2"><Camera className="w-5 h-5" /><input type="file" accept="image/*" onChange={e => e.target.files[0] && upload(e.target.files[0])} className="hidden" disabled={uploading} /></label>}
          <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder={user ? (uploading ? "Upload..." : "Votre message...") : "Connectez-vous"} disabled={!user}
            className="flex-1 px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F] disabled:bg-[#F5F1EA]" />
          <button onClick={send} disabled={(!text.trim() && !msgImg) || sending || !user} className="bg-[#1F4D3F] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{sending ? "..." : "Envoyer"}</button>
        </div>
      </div>
    </div>
  );
};

const DetailModal = ({ listing, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showChat, setShowChat] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const images = listing.images || [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="relative">
            <div className="h-64 bg-[#F5F1EA] flex items-center justify-center overflow-hidden rounded-t-2xl">
              {images.length > 0 ? <img src={img(images[activeImg])} alt={listing.title} className="w-full h-full object-contain" /> : <Package className="w-14 h-14 text-[#C9BFAE]" />}
            </div>
            <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-xl shadow">×</button>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto bg-[#FAFAF8]">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={"w-16 h-16 rounded-lg overflow-hidden shrink-0 border-2 " + (activeImg === i ? "border-[#1F4D3F]" : "border-transparent")}>
                  <img src={img(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="p-6">
            <h1 className="font-serif text-2xl font-semibold text-[#1F4D3F] mb-2">{listing.title}</h1>
            {listing.price
              ? <div className="text-3xl font-bold text-[#C75B27] mb-4">{Math.round(listing.price).toLocaleString("fr")} DA {listing.price_negotiable && <span className="text-sm font-normal text-[#9A8F82]">(négociable)</span>}</div>
              : <div className="text-lg text-[#9A8F82] mb-4">Prix sur demande</div>}

            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              {listing.brand && <div><span className="text-[#9A8F82]">Marque :</span> <strong>{listing.brand}</strong></div>}
              {listing.condition && <div><span className="text-[#9A8F82]">État :</span> <strong>{CONDITION_LABELS[listing.condition]}</strong></div>}
              {listing.wilaya && <div><span className="text-[#9A8F82]">Wilaya :</span> <strong>{listing.wilaya}</strong></div>}
              {listing.city && <div><span className="text-[#9A8F82]">Ville :</span> <strong>{listing.city}</strong></div>}
            </div>

            {(listing.services || []).length > 0 && (
              <div className="mb-4">
                <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Services inclus</h3>
                <div className="flex flex-wrap gap-2">
                  {listing.services.map(s => <span key={s} className="text-sm bg-[#E4EDE9] text-[#1F4D3F] px-3 py-1.5 rounded-full">{SERVICE_LABELS[s] || s}</span>)}
                </div>
              </div>
            )}

            {listing.description && (
              <div className="mb-6">
                <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Description</h3>
                <p className="text-sm text-[#5A5247] bg-[#F5F1EA] rounded-xl p-4 whitespace-pre-wrap">{listing.description}</p>
              </div>
            )}

            <div className="flex items-center gap-3 mb-6 p-3 bg-[#E4EDE9] rounded-xl">
              <div className="w-11 h-11 rounded-full bg-[#1F4D3F] flex items-center justify-center text-white font-bold">{(listing.seller_name || "?")[0].toUpperCase()}</div>
              <div>
                <div className="font-medium text-sm">{listing.seller_name}</div>
                <div className="text-xs text-[#7A6F5C]">{listing.seller_role === "supplier" ? "Fournisseur" : "Vendeur"}</div>
              </div>
            </div>

            <button onClick={() => user ? setShowChat(true) : navigate("/connexion")}
              className="w-full bg-[#C75B27] hover:bg-[#a84d21] text-white py-3 rounded-xl font-semibold text-sm transition-colors">
              Contacter le vendeur
            </button>
          </div>
        </div>
      </div>
      {showChat && <ChatBox listing={listing} onClose={() => setShowChat(false)} />}
    </>
  );
};

const NewListingForm = ({ onClose, onSuccess }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ type:"occasion", title:"", description:"", price:"", price_negotiable:false, wilaya:"", city:"", brand:"", condition:"bon", contact_phone:"", services:[] });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const toggleService = (s) => setForm(f => ({...f, services: f.services.includes(s) ? f.services.filter(x=>x!==s) : [...f.services,s]}));

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const token = localStorage.getItem("dz_token");
    const urls = [];
    for (const file of files.slice(0, 4)) {
      const data = new FormData(); data.append("file", file);
      const r = await fetch(API + "/api/upload", { method: "POST", headers: { "Authorization": "Bearer " + token }, body: data });
      if (r.ok) { const d = await r.json(); urls.push(d.url); }
    }
    setImages(prev => [...prev, ...urls]);
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title || !form.wilaya) { toast({ title:"Erreur", description:"Titre et wilaya obligatoires", variant:"destructive" }); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/listings", {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({...form, price: form.price ? parseFloat(form.price) : null, images}),
      });
      const d = await r.json();
      if (r.ok) { toast({ title:"✅ Annonce publiée !" }); onSuccess?.(); onClose(); }
      else toast({ title:"Erreur", description:d.detail||"Erreur", variant:"destructive" });
    } catch {}
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-[#1F4D3F] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-white font-semibold text-lg">Déposer une annonce</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Titre *</label>
            <input value={form.title} onChange={e => set("title",e.target.value)} placeholder="ex: Fauteuil dentaire Stern Weber S200" className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]" /></div>
          <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Description</label>
            <textarea value={form.description} onChange={e => set("description",e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none resize-none" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Marque</label>
              <input value={form.brand} onChange={e => set("brand",e.target.value)} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" /></div>
            <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">État</label>
              <select value={form.condition} onChange={e => set("condition",e.target.value)} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm bg-white">
                {Object.entries(CONDITION_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-2">Services inclus (optionnel)</label>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(SERVICE_LABELS).map(([k,v]) => (
                <button key={k} onClick={() => toggleService(k)} className={"p-3 rounded-xl border text-sm font-medium text-left " + (form.services.includes(k) ? "bg-[#E4EDE9] text-[#1F4D3F] border-[#1F4D3F]" : "bg-white border-[#E8E0D0] text-[#5A5247]")}>{v}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Prix (DA)</label>
              <input value={form.price} onChange={e => set("price",e.target.value)} type="number" className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm" /></div>
            <div className="flex items-center gap-2 pt-7">
              <input type="checkbox" id="nego" checked={form.price_negotiable} onChange={e => set("price_negotiable",e.target.checked)} className="w-4 h-4 accent-[#1F4D3F]" />
              <label htmlFor="nego" className="text-sm text-[#5A5247]">Négociable</label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Wilaya *</label>
              <select value={form.wilaya} onChange={e => set("wilaya",e.target.value)} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm bg-white">
                <option value="">Sélectionner...</option>
                {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
              </select></div>
            <div><label className="text-sm font-medium text-[#2D2A26] block mb-1">Téléphone</label>
              <input value={form.contact_phone} onChange={e => set("contact_phone",e.target.value)} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm" /></div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-2">Photos (max 4)</label>
            <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-[#E8E0D0] rounded-xl p-4 hover:border-[#1F4D3F]">
              <Camera className="w-6 h-6 text-[#7A6F5C]" />
              <div><div className="text-sm font-medium text-[#2D2A26]">{uploading ? "Upload..." : "Ajouter des photos"}</div><div className="text-xs text-[#9A8F82]">JPG, PNG — max 5MB</div></div>
              <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" disabled={uploading} />
            </label>
            {images.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {images.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={img(url)} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#E8E0D0]" />
                    <button onClick={() => setImages(imgs => imgs.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={submit} disabled={saving || uploading} className="w-full bg-[#1F4D3F] text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50">{saving ? "Envoi..." : "Publier l'annonce"}</button>
        </div>
      </div>
    </div>
  );
};

const Occasion = () => {
  useDocumentMeta({
    title: "Matériel dentaire d'occasion en Algérie — Achat & Vente | Comptoir Online",
    description: "Achetez et vendez du matériel dentaire d'occasion entre professionnels en Algérie. Vente sécurisée avec SAV et livraison.",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterWilaya, setFilterWilaya] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      let url = API + "/api/occasion/listings?limit=50";
      if (filterWilaya) url += "&wilaya=" + encodeURIComponent(filterWilaya);
      if (search) url += "&q=" + encodeURIComponent(search);
      const r = await fetch(url);
      const d = await r.json();
      setListings(d.listings || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [filterWilaya, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F] mb-1">Matériel d'occasion</h1>
          <p className="text-[#7A6F5C] text-sm">Achetez et vendez du matériel dentaire entre professionnels</p>
        </div>
        <button onClick={() => user ? setShowForm(true) : navigate("/connexion")} className="bg-[#C75B27] hover:bg-[#a84d21] text-white px-6 py-3 rounded-xl font-semibold text-sm">+ Déposer une annonce</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un matériel..." className="px-4 py-2 border border-[#E8E0D0] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F] min-w-[200px]" />
        <select value={filterWilaya} onChange={e => setFilterWilaya(e.target.value)} className="px-4 py-2 border border-[#E8E0D0] rounded-full text-sm bg-white focus:outline-none">
          <option value="">Toutes les wilayas</option>
          {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{Array.from({length:8}).map((_,i) => <div key={i} className="h-64 bg-[#F5F1EA] rounded-2xl animate-pulse" />)}</div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20">
          <div className="flex justify-center mb-4"><Package className="w-14 h-14 text-[#C9BFAE]" /></div>
          <h2 className="text-xl font-semibold text-[#1F4D3F] mb-2">Aucune annonce pour l'instant</h2>
          <button onClick={() => user ? setShowForm(true) : navigate("/connexion")} className="bg-[#C75B27] text-white px-6 py-3 rounded-xl font-semibold text-sm mt-4">Déposer une annonce</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listings.map(l => <ListingCard key={l.id} listing={l} onClick={(l) => navigate('/occasion/' + l.id)} />)}
        </div>
      )}
      {showForm && <NewListingForm onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};

export default Occasion;
